<template>
  <SAppContainer>
    <LocalSearchBreadcrumbs
      v-if="localSearchRouteParams"
      :customer-name
      :route-params="localSearchRouteParams"
    />
    <SBreadcrumbsSeo
      v-else
      :breadcrumbs="[
        {
          label: tr('main_site.company_wall.breadcrumbs.home'),
          href: ziggy('mainSite.homepage'),
        },
        {
          label: tr('main_site.company_wall.breadcrumbs.company_wall'),
          href: ziggy('mainSite.companyWall', { page: undefined }),
        },
        { label: customerName },
      ]"
    />
  </SAppContainer>

  <SAppContainer class="company-wall-details">
    <template v-if="isLoading">
      <div class="company-wall-details__content">
        <CompanyWallDetailsContentSkeleton />
        <section>
          <CompanyWallDetailsReviewsSkeleton />
        </section>
      </div>
      <CompanyWallDetailsCtaSkeleton class="company-wall-details__aside" />
    </template>

    <SErrorMessage
      v-else-if="state.status === 'error'"
      retry
      size="lg"
      @retry="refresh"
    />

    <template v-else-if="state.status === 'success'">
      <div class="company-wall-details__content">
        <CompanyWallDetailsContent
          :customer-name="customerName"
          :data="state.data"
        />

        <CompanyWallDetailsReviews
          id="company-wall-details-reviews"
          :customer-id="state.data.companyCard.id"
          :customer-name="customerName"
          :customer-structured-data-id="canonical"
          :review-description="state.data.reviewDescription"
        />
      </div>
      <CompanyWallDetailsCta
        class="company-wall-details__aside"
        :customer-id="state.data.companyCard.id"
        :customer-name="customerName"
        data-gtm-container="company-wall-sidebar"
        :geo-product-categories="state.data.geoProductCategories"
        :usps="state.data.usps"
      />
    </template>
  </SAppContainer>

  <template v-if="isLoading">
    <CompanyWallDetailsInternalLinksSkeleton />
  </template>
  <CompanyWallDetailsInternalLinks
    v-else-if="internalLinksData"
    :company-id="internalLinksData.companyId"
    :municipality-name="internalLinksData.municipalityName"
    :municipality-slug="internalLinksData.municipalitySlug"
    :product-slug="internalLinksData.geoProductSlug"
    :province-slug="internalLinksData.provinceSlug"
  />
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import {
  callWithNuxt,
  isNuxtError,
  navigateTo,
  useNuxtApp,
  useRoute,
} from "#app";
import { pick } from "radash";
import { computed, onServerPrefetch, watchEffect } from "vue";

import { SAppContainer, SErrorMessage } from "@solvari/components";
import {
  injectI18nLocale,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import { ziggy } from "@/shared/libs/ziggy";
import {
  RecentlyDeletedCustomerError,
  useCompanyWallData,
} from "~/features/company-wall/api/details.api.ts";
import CompanyWallDetailsContent from "~/features/company-wall/components/company-wall-details/content/CompanyWallDetailsContent.vue";
import CompanyWallDetailsContentSkeleton from "~/features/company-wall/components/company-wall-details/content/CompanyWallDetailsContentSkeleton.vue";
import CompanyWallDetailsCta from "~/features/company-wall/components/company-wall-details/cta/CompanyWallDetailsCta.vue";
import CompanyWallDetailsCtaSkeleton from "~/features/company-wall/components/company-wall-details/cta/CompanyWallDetailsCtaSkeleton.vue";
import CompanyWallDetailsInternalLinks from "~/features/company-wall/components/company-wall-details/internal-links/CompanyWallDetailsInternalLinks.vue";
import CompanyWallDetailsInternalLinksSkeleton from "~/features/company-wall/components/company-wall-details/internal-links/CompanyWallDetailsInternalLinksSkeleton.vue";
import LocalSearchBreadcrumbs from "~/features/company-wall/components/company-wall-details/LocalSearchBreadcrumbs.vue";
import CompanyWallDetailsReviews from "~/features/company-wall/components/company-wall-details/reviews/CompanyWallDetailsReviews.vue";
import CompanyWallDetailsReviewsSkeleton from "~/features/company-wall/components/company-wall-details/reviews/CompanyWallDetailsReviewsSkeleton.vue";
import { useCompanyWallDetailsSeo } from "~/features/company-wall/composables/useCompanyWallDetailsSeo.ts";
import SBreadcrumbsSeo from "~/shared/components/SBreadcrumbsSeo.vue";
import { usePageContext } from "~/shared/composables/usePageContext.ts";

const localeCode = injectI18nLocale().value;
const { tr } = useI18nMainSiteCompanyWall();
const route = useRoute();
const nuxtApp = useNuxtApp();
type Params = {
  geoProductSlug?: string;
  municipality?: string;
  province?: string;
  slug: string;
};
const routeParams = pick(route.params, [
  "geoProductSlug",
  "province",
  "municipality",
  "slug",
]) as Params;

usePageContext({ pageType: "company_wall_details" });

function hasLocalSearchParams(params: Params): params is Required<Params> {
  return !!params.municipality && !!params.province && !!params.geoProductSlug;
}

const localSearchRouteParams = computed(() =>
  hasLocalSearchParams(routeParams) ? routeParams : null,
);

const { state, refresh, isLoading } = useQuery(
  useCompanyWallData({
    customerSubmunicipalitySlug: routeParams.slug,
    localeCode,
    productSlug: routeParams.geoProductSlug,
    provinceSlug: routeParams.province,
    municipalitySlug: routeParams.municipality,
  }),
);

function navigateToDeletedCompanyRedirect() {
  const localSearchParams = localSearchRouteParams.value;
  const redirectPath = localSearchParams
    ? ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: localSearchParams.geoProductSlug,
        province: localSearchParams.province,
        municipality: localSearchParams.municipality,
      })
    : ziggy("mainSite.companyWall", { page: undefined });

  return navigateTo(
    { path: redirectPath, query: { companyDeleted: "1" } },
    { redirectCode: 301 },
  );
}

watchEffect(() => {
  if (!(state.value.error instanceof RecentlyDeletedCustomerError)) {
    return;
  }

  void navigateToDeletedCompanyRedirect();
});

onServerPrefetch(async () => {
  await refresh();

  // ssrCatchError swallows errors; a 404 must still reach the error page.
  if (isNuxtError(state.value.error) && state.value.error.statusCode === 404) {
    throw state.value.error;
  }

  if (!(state.value.error instanceof RecentlyDeletedCustomerError)) {
    return;
  }

  await callWithNuxt(nuxtApp, () => navigateToDeletedCompanyRedirect());
});

const customerName = computed(
  () => state.value.data?.companyCard.name ?? routeParams.slug,
);

const internalLinksData = computed(() => {
  const data = state.value.data;
  if (!data) {
    return null;
  }

  return {
    geoProductSlug:
      routeParams.geoProductSlug ??
      data.canonicalGeoProductCategorySlug ??
      undefined,
    provinceSlug: routeParams.province ?? data.provinceSlug ?? undefined,
    municipalitySlug:
      routeParams.municipality ?? data.municipalitySlug ?? undefined,
    // Route municipality → name looked up from it, so heading and links name one town
    municipalityName: routeParams.municipality
      ? undefined
      : (data.municipalityName ?? undefined),
    companyId: data.companyCard.id,
  };
});

const { canonical } = useCompanyWallDetailsSeo({
  customerName,
  data: computed(() => state.value.data),
  routeSlug: routeParams.slug,
});
</script>

<style lang="postcss">
.company-wall-details {
  @apply grid pb-4;
  grid-template:
    "content"
    "cta-btn"
    / minmax(0, 900px);

  &__content {
    @apply flex flex-col gap-16 area-[content];
  }

  &__aside {
    @apply area-[cta-btn];
  }

  @screen 900 {
    grid-template:
      "content . aside"
      / minmax(0, 760px) theme("spacing.10") theme("spacing.88");

    &__aside {
      @apply area-[aside];
    }
  }

  .s-heading {
    @apply text-primary-700;
  }
}
</style>
